import React, { useCallback, useEffect, useState } from 'react';
import apis from '@src/apis';
import { STEPS } from '@src/constants/instantVoiceCloning';
import { useDispatch, useSelector } from 'react-redux';
import actions from '@src/redux/actions';
import { clearInstantVoiceTraining } from '@src/redux/instantVoiceTraining/actions';
import { sortProvinces } from '@src/utils/province';
import { StyledVoiceCloneInput, Wrapper } from './index.style';
import Header from './Header';
import VoiceSampleUploader from './VoiceSampleUploader';
import PreviewAudio from './PreviewAudio';
import VoiceRecord from './VoiceRecord';
import CreateVoice from './CreateVoice';

const VoiceCloneInput = ({ refreshVoices, closePopupInstantVoiceCloning }) => {
  const dispatch = useDispatch();
  const { provinces: provincesStore } = useSelector((state) => state.province);

  const [metadataAudio, setMetadataAudio] = useState(null);
  const [sentences, setSentences] = useState(null);
  const [loadingSentences, setLoadingSentences] = useState(false);
  const [audioDraft, setAudioDraft] = useState(null);

  const onSetMetadataAudio = useCallback(
    (data) => {
      setMetadataAudio(data);
    },
    [metadataAudio],
  );

  const [step, setStep] = useState(STEPS.UPLOAD_AUDIO);
  const [prevStep, setPrevStep] = useState(null);
  const onSetStep = useCallback(
    (newStep) => {
      setPrevStep(step);
      setStep(newStep);
    },
    [step],
  );

  const onResetMetadataAudio = useCallback(() => {
    setMetadataAudio(null);
  }, []);

  const fetchSentences = async () => {
    setLoadingSentences(true);
    const res = await apis.instantVoiceTraining.getInstantVoiceTrainingDemo();
    if (res.status) {
      setSentences(res.result.sentences);
    }
    setLoadingSentences(false);
  };

  const fetchProvinces = async () => {
    const response = await apis.voiceCloning.getVoiceCloningProvinces({
      searchFields: 'name,code,locale',
    });
    if (!response?.status) return;

    const sortedProvinces = sortProvinces(response?.result?.provinces);

    dispatch(actions.province.addProvinces(sortedProvinces));
  };

  useEffect(() => {
    if (step === STEPS.RECORD_AUDIO) {
      fetchSentences();
    }
  }, [step]);

  useEffect(() => {
    if (!provincesStore.length) fetchProvinces();
  }, []);

  const handleClosePopupInstantVoiceCloning = () => {
    closePopupInstantVoiceCloning();
    // Reset state when close popup
    setStep(STEPS.UPLOAD_AUDIO);
    setMetadataAudio(null);
    setSentences(null);
    setAudioDraft(null);
    dispatch(clearInstantVoiceTraining());
  };

  const renderStep = () => {
    switch (step) {
      case STEPS.UPLOAD_AUDIO:
        return (
          <VoiceSampleUploader
            metadataAudio={metadataAudio}
            onSetMetadataAudio={onSetMetadataAudio}
            onSetStep={onSetStep}
          />
        );
      case STEPS.RECORD_AUDIO:
        return (
          <VoiceRecord
            metadataAudio={metadataAudio}
            onSetMetadataAudio={onSetMetadataAudio}
            onSetStep={onSetStep}
            sentences={sentences}
            fetchSentences={fetchSentences}
            loadingSentences={loadingSentences}
          />
        );
      case STEPS.PREVIEW_AUDIO:
        return (
          <PreviewAudio
            metadataAudio={metadataAudio}
            onSetStep={onSetStep}
            sentences={sentences}
            setAudioDraft={setAudioDraft}
            onResetMetadataAudio={onResetMetadataAudio}
            prevStep={prevStep}
          />
        );
      case STEPS.CREATE_VOICE_CLONE:
        return (
          <CreateVoice
            audioDraft={audioDraft}
            setAudioDraft={setAudioDraft}
            onSetStep={onSetStep}
            refreshVoices={refreshVoices}
            closePopupInstantVoiceCloning={handleClosePopupInstantVoiceCloning}
            onResetMetadataAudio={onResetMetadataAudio}
          />
        );
      default:
        return null;
    }
  };

  return (
    <StyledVoiceCloneInput>
      <Wrapper>
        <Header
          step={step}
          closePopupInstantVoiceCloning={handleClosePopupInstantVoiceCloning}
        />
        {renderStep()}
      </Wrapper>
    </StyledVoiceCloneInput>
  );
};
export default VoiceCloneInput;
